/**
 * cm-marquee.js — seamless marquee loop for sections/scrolling-banner.liquid.
 *
 * Custom element <cm-marquee>: fills .cm-marquee__track with enough
 * repetitions of its original items to cover the wrapper, then clones the
 * whole track (aria-hidden, links untabbable) so the base.css animation
 * loops without a gap. Speed comes from data-speed-desktop /
 * data-speed-mobile (px per second; 91/85 per home-gaps Q5) and is applied
 * as --cm-marquee-duration on this element.
 *
 * With prefers-reduced-motion (or CM.config.motion === false) the element
 * stays static: base.css shows the wrapped original strip instead.
 * Rebuilds on resize (debounced 200 ms) via ResizeObserver when available.
 */
'use strict';

(function () {
  const REBUILD_DEBOUNCE = 200;
  const MAX_REPETITIONS = 12;
  const DEFAULT_SPEED = 91; // px per second, desktop (home-gaps Q5)
  // Highlight delay comes per mark from data-highlight-delay (Jlood: 700ms).

  // fid-e9 — review chip: the rating/count are computed from the consented
  // review blocks of the reviews section (per-card --cm-rating-fill is
  // rating*20), never typed. Runs BEFORE the custom element upgrades so the
  // filled chip is part of the captured track HTML (clones included). A chip
  // whose section has no consented ratings stays hidden (honesty rule).
  function fillReviewChips() {
    const chips = document.querySelectorAll('[data-cm-review-chip][hidden]');
    if (!chips.length) return;
    const ratingsByKey = {};
    Array.prototype.forEach.call(chips, function (chip) {
      const key = chip.getAttribute('data-reviews-section') || 'reviews';
      if (!(key in ratingsByKey)) {
        // JSON-template wrappers are `shopify-section-template-…__<key>`;
        // plain templates use `shopify-section-<key>`.
        let section = document.getElementById('shopify-section-' + key);
        if (!section) {
          try { section = document.querySelector('[id$="__' + key + '"]'); } catch (e) { section = null; }
        }
        const ratings = [];
        if (section) {
          Array.prototype.forEach.call(section.querySelectorAll('[data-cm-rev] .cm-rating__stars'), function (el) {
            const match = /--cm-rating-fill:\s*([\d.]+)%/.exec(el.getAttribute('style') || '');
            const rating = match ? parseFloat(match[1]) / 20 : NaN;
            if (rating > 0 && rating <= 5) ratings.push(rating);
          });
          // G-fix 5: cards beyond `initial_cards` are parked in
          // <template data-cm-rev-rest> until the carousel is near the viewport
          // (cm-reviews.js removes the template once cloned, so no double count).
          // The average + count must cover ALL real reviews, not just the first 6.
          Array.prototype.forEach.call(section.querySelectorAll('template[data-cm-rev-rest]'), function (tpl) {
            if (!tpl.content) return;
            Array.prototype.forEach.call(tpl.content.querySelectorAll('[data-cm-rev] .cm-rating__stars'), function (el) {
              const match = /--cm-rating-fill:\s*([\d.]+)%/.exec(el.getAttribute('style') || '');
              const rating = match ? parseFloat(match[1]) / 20 : NaN;
              if (rating > 0 && rating <= 5) ratings.push(rating);
            });
          });
          // Keep the chip link honest: point it at the wrapper id that
          // actually exists on this template.
          const href = chip.getAttribute('href') || '';
          if (href.charAt(0) === '#' && !document.getElementById(href.slice(1))) {
            chip.setAttribute('href', '#' + section.id);
          }
        }
        ratingsByKey[key] = ratings;
      }
      const ratings = ratingsByKey[key];
      if (!ratings.length) return; // no consented ratings → chip stays hidden
      const count = ratings.length;
      const avg = (ratings.reduce(function (a, b) { return a + b; }, 0) / count).toFixed(1);
      const ratingEl = chip.querySelector('[data-cm-chip-rating]');
      if (ratingEl) ratingEl.textContent = avg;
      const labelEl = chip.querySelector('[data-cm-chip-label]');
      if (labelEl) {
        const template = labelEl.getAttribute('data-template') || '';
        labelEl.textContent = template.replace('{{ count }}', String(count));
      }
      chip.setAttribute('aria-label', (chip.getAttribute('data-aria-template') || '')
        .replace('{{ rating }}', avg)
        .replace('{{ count }}', String(count)));
      chip.removeAttribute('aria-hidden');
      chip.removeAttribute('hidden');
    });
  }
  fillReviewChips();

  class CMMarquee extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
        (window.CM && window.CM.config && window.CM.config.motion === false);
      if (reduced) return; // static strip, handled by the reduced-motion CSS

      this._track = this.querySelector('.cm-marquee__track');
      this._wrap = this._track ? this._track.parentElement : null;
      if (!this._track || !this._wrap) return;

      this._orig = this._track.innerHTML;
      this._buildTimer = null;
      this._highlightTimer = null;
      this._build = this._build.bind(this);
      this._debounce = this._debounce.bind(this);

      if (typeof ResizeObserver === 'function') {
        this._observer = new ResizeObserver(this._debounce);
        this._observer.observe(this._wrap);
      } else {
        this._onResize = this._debounce;
        window.addEventListener('resize', this._onResize);
      }

      this._build();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        this._onResize = null;
      }
      clearTimeout(this._buildTimer);
      clearTimeout(this._highlightTimer);
      if (this._hlObserver) {
        this._hlObserver.disconnect();
        this._hlObserver = null;
      }
      delete this.dataset.ready;
    }

    _debounce() {
      clearTimeout(this._buildTimer);
      this._buildTimer = setTimeout(this._build, REBUILD_DEBOUNCE);
    }

    _build() {
      const previousClone = this.querySelector('.cm-marquee__track[aria-hidden="true"]');
      if (previousClone) previousClone.remove();

      const track = this._track;
      track.innerHTML = this._orig; // restore the original item set
      const source = Array.prototype.slice.call(track.children);
      if (!source.length) return;

      // Repeat the original items until the track covers the wrapper.
      let repetitions = 0;
      while (track.scrollWidth < this._wrap.clientWidth && repetitions < MAX_REPETITIONS) {
        source.forEach((item) => {
          const copy = item.cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.querySelectorAll('a, button, [tabindex], input, select, textarea').forEach((el) => el.setAttribute('tabindex', '-1'));
          track.appendChild(copy);
        });
        repetitions += 1;
      }

      // Second track for the seamless loop (base.css animates both).
      const clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, [tabindex], input, select, textarea').forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      track.insertAdjacentElement('afterend', clone);

      // The original track's animation started at first paint, the clone's
      // starts now — out of phase they drift apart and the seam shows
      // (duplicated/missing items). Restart both in the same frame so they
      // run locked together.
      [track, clone].forEach((el) => {
        el.style.animation = 'none';
        void el.offsetWidth; // flush so the restart takes effect
        el.style.animation = '';
      });

      // px/s -> seconds; the CSS animation-duration consumes the variable.
      const speed = (window.innerWidth < 768
        ? Number(this.dataset.speedMobile)
        : Number(this.dataset.speedDesktop)) || DEFAULT_SPEED;
      const duration = track.scrollWidth / speed;
      this.style.setProperty('--cm-marquee-duration', duration.toFixed(2) + 's');

      // Highlight words (G003, Jlood xHighlightText): each mark gets .is-active
      // when it enters the viewport (IntersectionObserver, Jlood rootMargin
      // "0px 0px -20px 0px") after its data-highlight-delay (700ms), so the
      // sweep staggers across the strip; marks already in view at init fire
      // immediately. CSS sweeps the squiggle clip-path / marker gradient.
      clearTimeout(this._highlightTimer);
      if (this._hlObserver) this._hlObserver.disconnect();
      this._hlObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const mark = entry.target;
          this._hlObserver.unobserve(mark);
          const delay = parseInt(mark.getAttribute('data-highlight-delay'), 10) || 0;
          setTimeout(() => mark.classList.add('is-active'), delay);
        });
      }, { rootMargin: '0px 0px -20px 0px' });
      this.querySelectorAll('.cm-scrolling-banner__hl').forEach((mark) => {
        this._hlObserver.observe(mark);
      });
    }
  }

  if (!customElements.get('cm-marquee')) customElements.define('cm-marquee', CMMarquee);
})();
